import React, { useMemo, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import OBDDataService from "../../services/OBDDataService";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  Brush,
} from "recharts";

/* ============================================================
   HELPERS
============================================================ */

/**
 * Numeric value = IMEI
 * Alphanumeric value = Registration Number
 */
const getSearchParams = (value) => {
  const searchValue = value.trim();

  if (!searchValue) {
    return null;
  }

  if (/^\d+$/.test(searchValue)) {
    return {
      imei: searchValue,
    };
  }

  return {
    reg_no: searchValue,
  };
};
const PARAMETER_COLORS = [
  "#1976d2",
  "#d32f2f",
  "#388e3c",
  "#f57c00",
  "#7b1fa2",
  "#0097a7",
  "#c2185b",
  "#455a64",
  "#5d4037",
  "#303f9f",
  "#689f38",
  "#e64a19",
  "#00838f",
  "#6a1b9a",
  "#ad1457",
  "#2e7d32",
];

const getParameterColor = (parameter, index) => {
  const hue = (index * 137.508) % 360;
  return `hsl(${hue}, 70%, 45%)`;
};

const OBD_PARAMETER_INFO = {
  RPM: {
    pid: "01 0C",
    label: "Engine Speed",
    description: "Engine speed in revolutions per minute.",
    interpretation: "Normal warm idle when the vehicle is stationary.",
  },

  SPD: {
    pid: "01 0D",
    label: "Vehicle Speed",
    description: "Vehicle road speed.",
    interpretation: "0 km/h means the vehicle is stationary.",
  },

  CLT: {
    pid: "01 05",
    label: "Engine Coolant Temperature",
    description: "Engine coolant temperature.",
    interpretation: "Around 85°C indicates a normally warmed-up engine.",
  },

  OIL: {
    pid: "01 5C",
    label: "Engine Oil Temperature",
    description: "Engine oil temperature.",
    interpretation:
      "0°C may indicate that this PID is unsupported or unavailable.",
  },

  MAP: {
    pid: "01 0B",
    label: "Intake Manifold Absolute Pressure",
    description: "Absolute pressure inside the intake manifold.",
    interpretation:
      "A lower pressure than atmospheric pressure is expected at idle.",
  },

  IAT: {
    pid: "01 0F",
    label: "Intake Air Temperature",
    description: "Temperature of the intake air.",
    interpretation:
      "A warm reading can occur because of engine-bay heat soak.",
  },

  LOAD: {
    pid: "01 04",
    label: "Calculated Engine Load",
    description: "ECU-calculated engine load.",
    interpretation:
      "Higher idle load can occur because of AC or electrical loads.",
  },

  TMG: {
    pid: "01 0E",
    label: "Ignition Timing Advance",
    description: "Ignition timing advance relative to TDC.",
    interpretation: "A small positive value is plausible at idle.",
  },

  THR: {
    pid: "01 11",
    label: "Absolute Throttle Position",
    description: "Normalized throttle position reported by the ECU.",
    interpretation:
      "The value does not necessarily represent physical throttle opening.",
  },

  PDL: {
    pid: "01 5A",
    label: "Accelerator Pedal Position",
    description: "Accelerator pedal position.",
    interpretation: "0% is expected when the accelerator is not pressed.",
  },

  CTHR: {
    pid: "01 4C",
    label: "Commanded Throttle Actuator",
    description: "Throttle opening requested by the ECU.",
    interpretation:
      "A small opening can be used by the ECU to maintain idle.",
  },

  DTQ: {
    pid: "01 61",
    label: "Driver Demand Engine Torque",
    description: "Engine torque requested by the driver.",
    interpretation:
      "0% can be expected at idle without accelerator input.",
  },

  ATQ: {
    pid: "01 62",
    label: "Actual Engine Torque",
    description: "Actual engine torque percentage.",
    interpretation:
      "0% may indicate an unsupported PID depending on the vehicle.",
  },

  STFT: {
    pid: "01 06",
    label: "Short-Term Fuel Trim Bank 1",
    description: "Short-term fuel correction for Bank 1.",
    interpretation:
      "A small positive value indicates the ECU is adding a small amount of fuel.",
  },

  LTFT: {
    pid: "01 07",
    label: "Long-Term Fuel Trim Bank 1",
    description: "Long-term learned fuel correction for Bank 1.",
    interpretation:
      "A value around +4% is generally a small fuel correction.",
  },

  BARO: {
    pid: "01 33",
    label: "Barometric Pressure",
    description: "Atmospheric/barometric pressure.",
    interpretation:
      "0 kPa is invalid for normal atmospheric pressure and may indicate an unsupported PID.",
  },

  O2V: {
    pid: "01 15",
    label: "Oxygen Sensor",
    description: "Oxygen sensor voltage/associated sensor value.",
    interpretation:
      "A downstream oxygen sensor voltage around 0.6 V can be plausible.",
  },

  O2T: {
    pid: "01 15",
    label: "Oxygen Sensor Trim",
    description: "Associated oxygen sensor fuel-trim value.",
    interpretation: "Interpret according to the decoder and supported PID.",
  },

  CEQ: {
    pid: "01 44",
    label: "Commanded Equivalence Ratio",
    description: "ECU commanded equivalence ratio.",
    interpretation:
      "A value close to 1.0 indicates a target near stoichiometric mixture.",
  },

  CAT: {
    pid: "01 3C",
    label: "Catalyst Temperature",
    description: "Catalyst temperature, Bank 1 Sensor 1.",
    interpretation:
      "Around 296°C is plausible for a warmed catalyst.",
  },

  O2LT: {
    pid: "01 56",
    label: "Secondary O₂ Long-Term Fuel Trim",
    description: "Secondary oxygen sensor long-term fuel trim.",
    interpretation:
      "0% may indicate no correction or an unsupported PID.",
  },

  GEAR: {
    pid: "01 A4",
    label: "Transmission Actual Gear",
    description: "Transmission gear/status information.",
    interpretation:
      "0 may represent Park/Neutral or may indicate unsupported decoding.",
  },

  FUEL: {
    pid: "01 2F",
    label: "Fuel Tank Level",
    description: "Fuel tank level percentage.",
    interpretation:
      "0% should be verified against the actual fuel level.",
  },

  FRATE: {
    pid: "01 5E",
    label: "Engine Fuel Consumption Rate",
    description: "Engine fuel consumption rate.",
    interpretation:
      "0.0 L/h while the engine is running may indicate an unsupported PID.",
  },

  VOLT: {
    pid: "01 42",
    label: "ECU Supply Voltage",
    description: "Control-module/ECU supply voltage.",
    interpretation:
      "Around 14.4 V is consistent with normal alternator charging.",
  },

  AMB: {
    pid: "01 46",
    label: "Ambient Air Temperature",
    description: "Ambient air temperature.",
    interpretation:
      "0°C should be verified against actual ambient conditions.",
  },

  REFTQ: {
    pid: "01 63",
    label: "Engine Reference Torque",
    description: "Reference torque used by the ECU.",
    interpretation:
      "0 Nm may indicate an unsupported PID.",
  },

  LAM: {
    pid: "01 34",
    label: "Actual Lambda",
    description: "Actual lambda/equivalence ratio.",
    interpretation:
      "A value close to 1.0 indicates an approximately stoichiometric mixture.",
  },

  O2MA: {
    pid: "01 34",
    label: "Wideband O₂ Pump Current",
    description: "Wideband oxygen sensor pump current.",
    interpretation:
      "A value close to 0 mA can occur when lambda is very close to 1.0.",
  },

  ABSLD: {
    pid: "01 43",
    label: "Absolute Engine Load",
    description: "Absolute engine load calculation.",
    interpretation:
      "This is a different SAE load calculation from calculated engine load.",
  },

  EVAP: {
    pid: "01 2E",
    label: "EVAP Purge",
    description: "Commanded evaporative-emission purge valve percentage.",
    interpretation:
      "A purge command around 33% can be normal under suitable operating conditions.",
  },
};

const getParameterInfo = (parameter) => {
  const key = String(parameter || "").toUpperCase();

  return (
    OBD_PARAMETER_INFO[key] || {
      pid: "N/A",
      label: parameter,
      description: "OBD parameter returned by the vehicle.",
      interpretation: "Interpretation is not available for this parameter.",
    }
  );
};

/**
 * Convert datetime-local to UTC ISO.
 */
const toISOStringUTC = (value) => {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return date.toISOString();
};

/**
 * Format date/time.
 */
const formatOBDDateTime = (timestamp) => {
  if (!timestamp) {
    return "-";
  }

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString();
};

/**
 * Get OBD parameter value.
 */
const getOBDValue = (record, parameter) => {
  if (!record) {
    return null;
  }

  return (
    record?.[parameter] ??
    record?.obd?.[parameter] ??
    null
  );
};

/**
 * Normalize API record.
 */
const normalizeOBDRecord = (record) => {
  if (!record || typeof record !== "object") {
    return null;
  }

  return {
    ...record,
    ...(record.obd || {}),

    timestamp:
      record.device_time ||
      record.received_at ||
      record.timestamp ||
      null,
  };
};

/**
 * Extract latest API record.
 */
const extractLatestRecord = (response) => {
  if (!response) {
    return null;
  }

  let record = null;

  if (
    response.data &&
    typeof response.data === "object" &&
    !Array.isArray(response.data)
  ) {
    record = response.data;
  } else if (
    response.result &&
    typeof response.result === "object" &&
    !Array.isArray(response.result)
  ) {
    record = response.result;
  } else if (
    typeof response === "object" &&
    !Array.isArray(response) &&
    (
      response.obd ||
      response.RPM !== undefined ||
      response.SPD !== undefined
    )
  ) {
    record = response;
  } else if (Array.isArray(response)) {
    record =
      response.length > 0
        ? response[response.length - 1]
        : null;
  }

  return normalizeOBDRecord(record);
};

/**
 * Extract history records.
 */
const extractHistoryRecords = (response) => {
  if (!response) {
    return [];
  }

  let records = [];

  if (Array.isArray(response)) {
    records = response;
  } else if (Array.isArray(response.data)) {
    records = response.data;
  } else if (Array.isArray(response.results)) {
    records = response.results;
  } else if (Array.isArray(response.records)) {
    records = response.records;
  } else if (Array.isArray(response.result)) {
    records = response.result;
  } else if (
    response.data &&
    Array.isArray(response.data.results)
  ) {
    records = response.data.results;
  } else if (
    response.data &&
    Array.isArray(response.data.records)
  ) {
    records = response.data.records;
  }

  return records
    .map(normalizeOBDRecord)
    .filter(Boolean);
};

/**
 * Parameters come ONLY from backend history response.
 */
const extractBackendParameters = (records) => {
  const parameterSet = new Set();

  records.forEach((record) => {
    if (
      record?.obd &&
      typeof record.obd === "object" &&
      !Array.isArray(record.obd)
    ) {
      Object.keys(record.obd).forEach((key) => {
        parameterSet.add(key);
      });
    }
  });

  return Array.from(parameterSet);
};

/**
 * Check numeric value.
 */
const isNumericValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return false;
  }

  return Number.isFinite(Number(value));
};


/* ============================================================
   COMPONENT
============================================================ */

const OBDData = () => {
  /* ==========================================================
     VEHICLE SEARCH
  ========================================================== */

  const [searchValue, setSearchValue] = useState("");

  const [vehicleSearchLoading, setVehicleSearchLoading] =
    useState(false);

  const [searched, setSearched] = useState(false);

  const [error, setError] = useState("");

  const [vehicleSearchParams, setVehicleSearchParams] =
    useState(null);


  /* ==========================================================
     LATEST DATE/TIME
  ========================================================== */

  const [latestDateTime, setLatestDateTime] =
    useState("");


  /* ==========================================================
     LATEST DATA
  ========================================================== */

  const [latestData, setLatestData] = useState(null);

  const [latestApiResponse, setLatestApiResponse] =
    useState(null);


  /* ==========================================================
     HISTORY DATA
  ========================================================== */

  const [historyData, setHistoryData] = useState([]);

  const [historyApiResponse, setHistoryApiResponse] =
    useState(null);

  const [historyLoading, setHistoryLoading] =
    useState(false);


  /* ==========================================================
     HISTORY DATE RANGE
  ========================================================== */

  const [startDateTime, setStartDateTime] =
    useState("");

  const [endDateTime, setEndDateTime] =
    useState("");


  /* ==========================================================
     PARAMETER
     
     SINGLE SELECT
  ========================================================== */

  const [selectedParameters, setSelectedParameters] =
    useState([]);


  /* ==========================================================
     BACKEND PARAMETERS
  ========================================================== */

  const backendParameters = useMemo(() => {
    return extractBackendParameters(historyData);
  }, [historyData]);


  /* ==========================================================
     VEHICLE SEARCH
     
     Calls latest API.
  ========================================================== */

  const handleVehicleSearch = async () => {
    const value = searchValue.trim();

    if (!value) {
      setError(
        "Please enter IMEI or Registration Number."
      );
      return;
    }

    const params = getSearchParams(value);

    if (!params) {
      setError(
        "Please enter a valid IMEI or Registration Number."
      );
      return;
    }

    setError("");
    setVehicleSearchLoading(true);

    // ONLY reset latest data
    setLatestData(null);
    setLatestApiResponse(null);

    try {
      const latestParams = {
        ...params,
      };

      if (latestDateTime) {
        latestParams.datetime =
          toISOStringUTC(latestDateTime);
      }

      console.log(
        "LATEST API PARAMS:",
        latestParams
      );

      const response =
        await OBDDataService.getLatestOBDData(
          latestParams
        );

      console.log(
        "LATEST API RESPONSE:",
        response.data
      );

      setLatestApiResponse(response.data);

      const record =
        extractLatestRecord(response.data);

      setLatestData(record);

      setSearched(true);

    } catch (err) {
      console.error(
        "Latest OBD search error:",
        err
      );

      setLatestData(null);
      setLatestApiResponse(null);

      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.response?.data?.error;

      setError(
        apiMessage ||
        "Unable to load latest OBD data."
      );

    } finally {
      setVehicleSearchLoading(false);
    }
  };


  /* ==========================================================
     HISTORY SEARCH
  ========================================================== */

  const handleHistorySearch = async () => {
    const value = searchValue.trim();

    if (!value) {
      setError(
        "Please enter IMEI or Registration Number."
      );
      return;
    }

    const params = getSearchParams(value);

    if (!params) {
      setError(
        "Please enter a valid IMEI or Registration Number."
      );
      return;
    }

    if (!startDateTime || !endDateTime) {
      setError(
        "Please select Start Date & Time and End Date & Time."
      );
      return;
    }

    const start = new Date(startDateTime);
    const end = new Date(endDateTime);

    if (end < start) {
      setError(
        "End Date & Time must be after Start Date & Time."
      );
      return;
    }

    const differenceHours =
      (end - start) / (1000 * 60 * 60);

    if (differenceHours > 48) {
      setError(
        "History date/time range cannot exceed 48 hours."
      );
      return;
    }

    setError("");
    setHistoryLoading(true);

    try {
      const historyParams = {
        ...params,

        start_datetime:
          toISOStringUTC(startDateTime),

        end_datetime:
          toISOStringUTC(endDateTime),
      };

      console.log(
        "HISTORY API PARAMS:",
        historyParams
      );

      const response =
        await OBDDataService.getHistoricalOBDData(
          historyParams
        );

      console.log(
        "HISTORY API RESPONSE:",
        response.data
      );

      setHistoryApiResponse(response.data);

      const records =
        extractHistoryRecords(response.data);

      setHistoryData(records);

      // Parameters ONLY from this History API response
      const parameters =
        extractBackendParameters(records);

      setSelectedParameters(
        parameters
      );

    } catch (err) {
      console.error(
        "History OBD search error:",
        err
      );

      setHistoryData([]);
      setHistoryApiResponse(null);
      setSelectedParameters([]);

      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.response?.data?.error;

      setError(
        apiMessage ||
        "Unable to load OBD data."
      );

    } finally {
      setHistoryLoading(false);
    }
  };


  /* ==========================================================
     LATEST PARAMETERS
  ========================================================== */

  const latestParameters = useMemo(() => {
    if (!latestData) {
      return [];
    }

    if (
      latestData.obd &&
      typeof latestData.obd === "object"
    ) {
      return Object.keys(latestData.obd);
    }

    return Object.keys(latestData).filter(
      (key) =>
        ![
          "imei",
          "reg_no",
          "received_at",
          "device_time",
          "timestamp",
          "obd",
        ].includes(key)
    );
  }, [latestData]);


  /* ==========================================================
     GRAPH DATA
  ========================================================== */

  const graphData = useMemo(() => {
    if (
      selectedParameters.length === 0 ||
      historyData.length === 0
    ) {
      return [];
    }

    return historyData
      .filter((record) => record?.timestamp)
      .map((record) => {
        const dataPoint = {
          timestamp: record.timestamp,
          time: formatOBDDateTime(record.timestamp),
        };

        selectedParameters.forEach((parameter) => {
          const value = getOBDValue(
            record,
            parameter
          );

          dataPoint[parameter] = isNumericValue(value)
            ? Number(value)
            : null;
        });

        return dataPoint;
      });
  }, [historyData, selectedParameters]);


  /* ==========================================================
     RESET
  ========================================================== */

  const handleReset = () => {
    setSearchValue("");

    setLatestDateTime("");

    setSearched(false);

    setLatestData(null);
    setLatestApiResponse(null);

    setHistoryData([]);
    setHistoryApiResponse(null);

    setVehicleSearchParams(null);

    setStartDateTime("");
    setEndDateTime("");

    setSelectedParameters([]);

    setError("");
  };


  /* ==========================================================
     RENDER
  ========================================================== */

  return (
    <Box
      sx={{
        p: {
          xs: 2,
          md: 3,
        },

        backgroundColor: "#f5f7fb",

        minHeight: "100vh",
      }}
    >

      {/* =====================================================
          HEADER
      ===================================================== */}

      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          fontWeight={600}
          gutterBottom
        >
          OBD Data
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
        >
          Search and analyze vehicle OBD
          diagnostic data
        </Typography>
      </Box>


      {/* =====================================================
          SEARCH VEHICLE
      ===================================================== */}

      <Card
        sx={{
          mb: 3,
          borderRadius: 2,
        }}
      >
        <CardContent>

          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ mb: 2 }}
          >
            Search Vehicle
          </Typography>


          {/* VEHICLE SEARCH */}

          <Grid
            container
            spacing={2}
            alignItems="center"
          >

            <Grid
              item
              xs={12}
              md={9}
            >

              <TextField

                fullWidth
                label="Search IMEI / Registration No"

                placeholder="Enter IMEI or Vehicle Registration Number"

                value={searchValue}

                onChange={(e) =>
                  setSearchValue(
                    e.target.value
                  )
                }

                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleVehicleSearch();
                  }
                }}
              />

            </Grid>


            <Grid
              item
              xs={12}
              md={2}
            >

              <Button
                fullWidth
                variant="contained"
                size="large"

                startIcon={
                  vehicleSearchLoading ? (
                    <CircularProgress
                      size={20}
                      color="inherit"
                    />
                  ) : (
                    <SearchIcon />
                  )
                }

                onClick={
                  handleVehicleSearch
                }

                disabled={
                  vehicleSearchLoading
                }
              >
                {vehicleSearchLoading
                  ? "Searching..."
                  : "Search"}
              </Button>

            </Grid>


            <Grid
              item
              xs={12}
              md={1}
            >

              <Button
                fullWidth
                variant="outlined"
                size="large"

                startIcon={
                  <RefreshIcon />
                }

                onClick={handleReset}
              >
                Reset
              </Button>

            </Grid>

          </Grid>


          {/* =================================================
              LATEST DATE/TIME
          ================================================= */}

          <Grid
            container
            spacing={2}
            sx={{ mt: 0.5 }}
          >

            <Grid
              item
              xs={12}
              md={4}
            >

              <TextField
                fullWidth

                type="datetime-local"

                label="Latest Date & Time"

                InputLabelProps={{
                  shrink: true,
                }}

                value={latestDateTime}

                onChange={(e) =>
                  setLatestDateTime(
                    e.target.value
                  )
                }

              />

            </Grid>

          </Grid>


          {error && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
            >
              {error}
            </Alert>
          )}

        </CardContent>
      </Card>


      {/* =====================================================
          LATEST DATA
      ===================================================== */}

      {searched && (
        <>

          <Card
            sx={{
              mb: 3,
              borderRadius: 2,
            }}
          >

            <CardContent>

              <Stack
                direction={{
                  xs: "column",
                  md: "row",
                }}

                justifyContent="space-between"

                alignItems={{
                  xs: "flex-start",
                  md: "center",
                }}

                sx={{ mb: 3 }}
              >

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >

                  <ShowChartIcon
                    color="primary"
                  />

                  <Typography
                    variant="h6"
                    fontWeight={600}
                  >
                    OBD Data
                  </Typography>

                </Stack>


                {latestData && (
                  <Stack
                    direction={{
                      xs: "column",
                      sm: "row",
                    }}
                    spacing={2}
                  >

                    <Typography
                      variant="body2"
                    >
                      <strong>IMEI:</strong>{" "}
                      {latestData.imei || "-"}
                    </Typography>

                    <Typography
                      variant="body2"
                    >
                      <strong>Reg No:</strong>{" "}
                      {latestData.reg_no || "-"}
                    </Typography>

                    <Typography
                      variant="body2"
                    >
                      <strong>Date:</strong>{" "}
                      {formatOBDDateTime(
                        latestData.timestamp
                      )}
                    </Typography>

                  </Stack>
                )}

              </Stack>


              {!latestData ? (

                <Alert severity="warning">
                  No OBD data is available
                  for this vehicle.
                </Alert>

              ) : latestParameters.length === 0 ? (

                <Alert severity="warning">
                  No OBD parameters were returned.
                </Alert>

              ) : (

                <Grid
                  container
                  spacing={2}
                >

                  {latestParameters.map(
                    (parameter) => {

                      const value =
                        getOBDValue(
                          latestData,
                          parameter
                        );

                      return (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          lg={2}
                          key={parameter}
                        >

                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              minHeight: 80,
                              borderTop: `4px solid ${getParameterColor(
                                parameter,
                                latestParameters.indexOf(parameter)
                              )}`,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                boxShadow: 3,
                              },
                            }}
                          >

                            {(() => {
                              const parameterInfo = getParameterInfo(parameter);
                              const parameterColor = getParameterColor(
                                parameter,
                                latestParameters.indexOf(parameter)
                              );

                              return (
                                <>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: parameterColor,
                                      fontWeight: 700,
                                      display: "block",
                                      mb: 0.5,
                                    }}
                                  >
                                    {parameter}
                                  </Typography>

                                  <Typography
                                    variant="subtitle2"
                                    fontWeight={600}
                                    sx={{ mb: 1 }}
                                  >
                                    {parameterInfo.label}
                                  </Typography>

                                  <Typography
                                    variant="h5"
                                    fontWeight={700}
                                    sx={{
                                      color: parameterColor,
                                      mb: 1,
                                    }}
                                  >
                                    {value !== undefined &&
                                      value !== null &&
                                      value !== ""
                                      ? value
                                      : "-"}
                                  </Typography>

                                  {/* <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          mb: 0.5,
        }}
      >
        <strong>PID:</strong> {parameterInfo.pid}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          lineHeight: 1.4,
          mb: 0.75,
        }}
      >
        {parameterInfo.description}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          display: "block",
          lineHeight: 1.4,
        }}
      >
        <strong>Interpretation:</strong>{" "}
        {parameterInfo.interpretation}
      </Typography> */}
                                </>
                              );
                            })()}

                          </Paper>

                        </Grid>
                      );
                    }
                  )}

                </Grid>

              )}

            </CardContent>

          </Card>


          {/* =================================================
              HISTORY SEARCH
          ================================================= */}

          <Card
            sx={{
              mb: 3,
              borderRadius: 2,
            }}
          >

            <CardContent>

              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ mb: 2 }}
              >
                OBD Data
              </Typography>


              <Grid
                container
                spacing={2}
                alignItems="center"
              >

                {/* START */}

                <Grid
                  item
                  xs={12}
                  md={3}
                >

                  <TextField
                    fullWidth

                    type="datetime-local"

                    label="Start Date & Time"

                    InputLabelProps={{
                      shrink: true,
                    }}

                    value={startDateTime}

                    onChange={(e) =>
                      setStartDateTime(
                        e.target.value
                      )
                    }
                  />

                </Grid>


                {/* END */}

                <Grid
                  item
                  xs={12}
                  md={3}
                >

                  <TextField
                    fullWidth

                    type="datetime-local"

                    label="End Date & Time"

                    InputLabelProps={{
                      shrink: true,
                    }}

                    value={endDateTime}

                    onChange={(e) =>
                      setEndDateTime(
                        e.target.value
                      )
                    }
                  />

                </Grid>


                {/* PARAMETER DROPDOWN */}

                <Grid
                  item
                  xs={12}
                  md={3}
                >

                  <FormControl
                    fullWidth
                    disabled={backendParameters.length === 0}
                  >
                    <InputLabel id="obd-parameter-label">
                      Select Parameters
                    </InputLabel>

                    <Select
                      labelId="obd-parameter-label"
                      multiple
                      value={selectedParameters}
                      label="Select Parameters"
                      onChange={(e) => {
                        const value = e.target.value;

                        setSelectedParameters(
                          typeof value === "string"
                            ? value.split(",")
                            : value
                        );
                      }}
                      renderValue={(selected) =>
                        selected.length > 0
                          ? selected.join(", ")
                          : "Select Parameters"
                      }
                    >
                      {backendParameters.map((parameter) => (
                        <MenuItem
                          key={parameter}
                          value={parameter}
                        >
                          {parameter}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                </Grid>


                {/* SEARCH */}

                <Grid
                  item
                  xs={12}
                  md={3}
                >

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"

                    startIcon={
                      historyLoading ? (
                        <CircularProgress
                          size={20}
                          color="inherit"
                        />
                      ) : (
                        <SearchIcon />
                      )
                    }

                    onClick={
                      handleHistorySearch
                    }

                    disabled={
                      historyLoading
                    }

                    sx={{
                      height: 56,
                    }}
                  >

                    {historyLoading
                      ? "Searching..."
                      : "Search"}

                  </Button>

                </Grid>

              </Grid>

            </CardContent>

          </Card>


          {/* =================================================
              HISTORY TABLE
          ================================================= */}

          <Card
            sx={{
              mb: 3,
              borderRadius: 2,
            }}
          >

            <CardContent>
              {historyData.length > 0 && (
                <Stack
                  direction="row"
                  spacing={3}
                  justifyContent="flex-end"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    <strong>IMEI:</strong>{" "}
                    {historyData[0]?.imei || "-"}
                  </Typography>

                  <Typography variant="body2">
                    <strong>Reg No:</strong>{" "}
                    {historyData[0]?.reg_no || "-"}
                  </Typography>
                </Stack>
              )}

              {historyData.length === 0 ? (

                <Alert severity="info">
                  Select the date/time range and click Search.
                </Alert>

              ) : (

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    maxHeight: 500,
                    overflowX: "auto",
                  }}
                >

                  <Table
                    stickyHeader
                    size="small"
                  >

                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Date & Time
                        </TableCell>


                        {selectedParameters.map((parameter, index) => (
                          <TableCell
                            key={parameter}
                            align="right"
                            sx={{
                              fontWeight: 700,
                              color: getParameterColor(
                                parameter,
                                index
                              ),
                            }}
                          >
                            {parameter}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>


                    <TableBody>
                      {historyData.map((row, index) => (
                        <TableRow
                          hover
                          key={`${row.timestamp}-${index}`}
                        >
                          <TableCell
                            sx={{
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatOBDDateTime(row.timestamp)}
                          </TableCell>


                          {selectedParameters.map((parameter) => {
                            const value = getOBDValue(
                              row,
                              parameter
                            );

                            return (
                              <TableCell
                                key={parameter}
                                align="right"
                              >
                                {value !== undefined &&
                                  value !== null &&
                                  value !== ""
                                  ? value
                                  : "-"}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>

                  </Table>

                </TableContainer>

              )}

            </CardContent>

          </Card>


          {/* =================================================
    LINE GRAPH
================================================= */}

          <Card
            sx={{
              borderRadius: 2,
              mb: 3,
            }}
          >
            <CardContent>

              <Box
                sx={{
                  mb: 3,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                  >
                    OBD Graph
                  </Typography>

                </Box>

                {historyData.length > 0 && (
                  <Stack
                    direction={{
                      xs: "column",
                      sm: "row",
                    }}
                    spacing={2}
                    alignItems={{
                      xs: "flex-start",
                      sm: "center",
                    }}
                  >
                    <Typography variant="body2">
                      <strong>IMEI:</strong>{" "}
                      {historyData[0]?.imei || "-"}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Reg No:</strong>{" "}
                      {historyData[0]?.reg_no || "-"}
                    </Typography>
                  </Stack>
                )}
              </Box>


              {selectedParameters.length === 0 ? (

                <Alert severity="info">
                  Select one or more parameters to display
                  the graph.
                </Alert>

              ) : graphData.length === 0 ? (

                <Alert severity="warning">
                  No numeric data available for the selected
                  parameters.
                </Alert>

              ) : (

                <>


                  {/* =====================================================
    ONE COMMON LINE GRAPH
===================================================== */}

                  <Box
                    sx={{
                      width: "100%",
                      height: 500,
                    }}
                  >
                    <ResponsiveContainer
                      width="100%"
                      height="100%"
                    >
                      <LineChart
                        data={graphData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 60,
                        }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                        />

                        <XAxis
                          dataKey="time"
                          minTickGap={50}
                          angle={-30}
                          textAnchor="end"
                          height={80}
                        />

                        <YAxis
                          allowDataOverflow
                          tickFormatter={(value) =>
                            Number(value).toFixed(2)
                          }
                        />

                        <ChartTooltip
                          contentStyle={{
                            borderRadius: 8,
                          }}
                        />

                        <Legend />

                        {selectedParameters.map(
                          (parameter, index) => (
                            <Line
                              key={parameter}
                              type="monotone"
                              dataKey={parameter}
                              name={parameter}
                              stroke={getParameterColor(
                                parameter,
                                index
                              )}
                              strokeWidth={2.5}
                              dot={false}
                              connectNulls
                            />
                          )
                        )}

                        {/* Graph zoom */}
                        <Brush
                          dataKey="time"
                          height={30}
                          stroke="#1976d2"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>

                </>

              )}

            </CardContent>
          </Card>

        </>
      )}

    </Box>
  );
};

export default OBDData;